<template>
  <items-batch-confirmation-dialog
    :execute="execute"
    item-id-key="displayId"
    :items="items"
    success-message="Recalls successfully initiated."
    :table-headers="tableHeaders"
    title="Recall"
    v-on="$listeners"
  >
    <template #description>
      You are about to recall <strong><pretty-number :value="totalQuantity" /></strong> shares
      across <strong><pretty-number :value="items.length" /></strong> loans:
    </template>
    <template #row="{ item }">
      <td>
        {{ item.counterpartyDisplay }}
      </td>
      <td>
        {{ item.security.ticker }}
      </td>
      <td>
        {{ item.security.cusip }}
      </td>
      <td class="text-end">
        <rate-output :rate="item.rate" :rate-modifier="item.rateModifier" />
      </td>
      <td class="text-end">
        <pretty-number :value="newQuantities[item.id] || 0" />
      </td>
      <td class="text-end">
        <pretty-number :value="item.openQuantityToRecall" />
      </td>
    </template>
  </items-batch-confirmation-dialog>
</template>

<script lang="ts">
import ItemsBatchConfirmationDialog from '@/modules/common/components/ItemsBatchConfirmationDialog.vue';
import { BorrowerOpenLoan, LenderOpenLoan, OpenLoanItem } from '@/modules/common/models';
import type { Api } from '@/modules/lender/types/lender-loans';
import { NewQuantities } from '@/modules/open-loans/helpers/multipleUpdates';
import Vue, { PropType } from 'vue';
import Component from 'vue-class-component';
type LoanRecallRequestItem = Api.LoanRecallRequestItem;

@Component({
  components: {
    ItemsBatchConfirmationDialog,
  },
  props: {
    items: Array as PropType<OpenLoanItem[]>,
    newQuantities: {
      type: Object as PropType<NewQuantities>,
      required: true,
      default: () => ({}),
    },
  },
})
export default class BatchRecallDialog extends Vue {
  protected readonly tableHeaders = [
    { text: 'Counterparty' },
    { text: 'Ticker' },
    { text: 'CUSIP' },
    { text: 'Rebate Rate', align: 'end' },
    { text: `Recall Quantity`, value: 'toUpdate', align: 'end' },
    { text: 'Available To Recall', value: 'openQuantityToRecall', align: 'end' },
  ];

  // props
  protected readonly items!: LenderOpenLoan[] | BorrowerOpenLoan[];
  protected readonly newQuantities!: NewQuantities;

  protected get totalQuantity(): number {
    return Object.values(this.newQuantities).reduce((acc, val) => acc + val, 0);
  }

  private async execute(): Promise<{ failedIds?: string[] }> {
    const recalls = this.items.map((loan: LenderOpenLoan | BorrowerOpenLoan) => {
      return {
        displayId: loan.displayId,
        quantity: this.newQuantities[loan.id],
      } as LoanRecallRequestItem;
    });
    const result = await this.$api.lenderLoans.batchRecallLenderLoan(recalls);
    const failedIds = result.failedItems.map((i) => i.requestId);

    // failing ids only, we are losing the error messages here
    return { failedIds };
  }
}
</script>
<style lang="scss" scoped></style>
